import { getLocaleFromPath } from "@/lib/i18n-request-locale";
import { defaultLocale, locales, type Locale } from "@/lib/i18n-config";

/**
 * Where a login lands when nothing in the flow asked for somewhere specific.
 * There is no unlocalized /dashboard route, so this always carries a locale.
 */
export function getDefaultPostLoginPath(locale: string) {
  return `/${resolveLocale(locale)}/dashboard`;
}

/**
 * Validates a caller-supplied redirect and falls back to the dashboard.
 *
 * Anything that is not a same-origin relative path is rejected: a protocol
 *-relative `//evil.com` would otherwise leave the site, and a `javascript:`
 * URL would run on the page that consumes it.
 */
export function resolvePostLoginPath(
  candidate: string | string[] | null | undefined,
  locale: string,
) {
  const raw = Array.isArray(candidate) ? candidate[0] : candidate;

  return isSafeRelativePath(raw) ? raw : getDefaultPostLoginPath(locale);
}

export function isSafeRelativePath(path: unknown): path is string {
  return (
    typeof path === "string" && path.startsWith("/") && !path.startsWith("//")
  );
}

/**
 * Locale for a post-login redirect, preferring the destination's own prefix.
 * Every intent-carrying `next` is locale-scoped already, so this resolves
 * without touching request headers in the common case.
 */
export function resolvePostLoginLocale(
  nextPath: string | null | undefined,
  fallback: Locale,
): Locale {
  return getLocaleFromPath(nextPath) ?? fallback;
}

function resolveLocale(locale: string): Locale {
  return locales.find((supported) => supported === locale) ?? defaultLocale;
}
